//

import { useState, useMemo, memo } from "react";

import { PublishHeaderType } from "./types";
import { formatDuration, shortDateTime } from "./util";
import CircleRed from "./CircleRed.svg";
import CircleGreen from "./CircleGreen.svg";
import CircleOrange from "./CircleOrange.svg";

import ImgProjectNotFound from "./ProjectNotFound.svg";
import ImgException from "./Exception.svg";

import "./PublishHeader.scss";

const STATE_IMAGES: Record<string, string> = {
  finished: CircleGreen,
  projectNotFound: ImgProjectNotFound,
  exception: ImgException,
};

const DEFAULT_STATE_IMAGE = CircleRed;

const PUBLISH_TYPE_SHORT: Record<string, string> = {
  FirstLanguage: "1.",
  NextLanguage: "n-th",
};

const LANGUAGE_THRESHOLD = 15;
const EPP_SIZE_THRESHOLD = 100;

interface PublishHeaderProps {
  items: PublishHeaderType[];
  onSelect: (publishId: string) => void;
  displayUTC: boolean;
  loading: boolean;
}

const PublishHeaderItem = memo(({
  item,
  isSelected,
  displayUTC,
  onClick,
}: {
  item: PublishHeaderType;
  isSelected: boolean;
  displayUTC: boolean;
  onClick: () => void;
}) => {
  const {
    state,
    startDate,
    finishDate,
    publishId,
    handleCount,
    name,
    folder,
    organizationId,
    eppSize,
    fileCount,
    language,
    publishType,
    isRecent,
    operationId,
  } = item;

  const stateImg = STATE_IMAGES[state] ?? DEFAULT_STATE_IMAGE;
  const isFatal = handleCount > 1;
  const publishTypeShort = PUBLISH_TYPE_SHORT[publishType] ?? "";
  const languageNum = Number(language);
  const eppSizeNum = Number(eppSize);

  const className = isSelected ? "item selected" : isRecent ? "item recent" : "item";

  return (
    <div className={className} key={publishId} onClick={onClick}>
      <div className="cell">
        <img src={stateImg} alt={state} height="12px" />
        {isFatal && (
          <img style={{ marginLeft: "1px" }} src={CircleOrange} alt={state} height="12px" />
        )}
      </div>
      <div className="cell">{handleCount}</div>
      <div className="cell">{shortDateTime(startDate, displayUTC)}</div>
      <div className="cell">{formatDuration(startDate, finishDate)}</div>
      <div className="cell">{publishTypeShort}</div>
      <div className="cell">
        <span style={{ color: languageNum > LANGUAGE_THRESHOLD ? "red" : undefined }}>
          {language}
        </span>
      </div>
      <div className="cell">{fileCount}</div>
      <div className="cell">
        <b style={{ color: eppSizeNum > EPP_SIZE_THRESHOLD ? "red" : undefined }}>
          {eppSize}
        </b>
        &nbsp;MB
      </div>
      <div className="cell">{organizationId}</div>
      <div className="cell">{folder}</div>
      <div className="cell">{name}</div>
      <div className="cell">{operationId}</div>
    </div>
  );
});

const PublishHeader = memo(({
  items,
  onSelect,
  displayUTC,
  loading,
}: PublishHeaderProps) => {
  const [currentPublishId, setCurrentPublishId] = useState<string>("");

  const handleClick = (publishId: string) => {
    setCurrentPublishId(publishId);
    onSelect(publishId);
  };

  if (items.length === 0 && !loading) {
    return <div>no publish project data found</div>;
  }

  return (
    <div className="publish-header">
      {items.map((item) => (
        <PublishHeaderItem
          key={item.publishId}
          item={item}
          isSelected={item.publishId === currentPublishId}
          displayUTC={displayUTC}
          onClick={() => handleClick(item.publishId)}
        />
      ))}
    </div>
  );
});

PublishHeader.displayName = "PublishHeader";

export default PublishHeader;
